import { useEffect, useMemo, useState } from "react";
import { FileEdit, Clock, CheckCircle2, XCircle, Ban, PackageCheck, FileSpreadsheet, ArrowUpDown } from "lucide-react";
import { inventoryApi, inventoryCategoryApi } from "../api";
import { InventoryPageHeader } from "../components/InventoryPageHeader";
import { InventoryPagination } from "../components/InventoryPagination";
import { InventoryStatusBadge } from "../components/InventoryStatusBadge";
import { InventoryError, InventoryLoading } from "../components/InventoryState";
import { useAsyncData } from "../hooks";
import { downloadXls } from "../utils/export";
import type { InventoryItem, InventoryRequestStatus, PageResponse } from "../types";

const STATUS_LIST: InventoryRequestStatus[] = [
  "DRAFT",
  "PENDING_APPROVAL",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
  "ISSUED",
];

const STATUS_META: Record<string, { icon: typeof FileEdit; tone: string }> = {
  DRAFT: { icon: FileEdit, tone: "text-slate-500 bg-slate-50" },
  PENDING_APPROVAL: { icon: Clock, tone: "text-amber-600 bg-amber-50" },
  APPROVED: { icon: CheckCircle2, tone: "text-emerald-600 bg-emerald-50" },
  REJECTED: { icon: XCircle, tone: "text-red-600 bg-red-50" },
  CANCELLED: { icon: Ban, tone: "text-red-600 bg-red-50" },
  ISSUED: { icon: PackageCheck, tone: "text-emerald-600 bg-emerald-50" },
};

/** Small "Export Excel" button, placed above a report table via the section header's action slot. */
function ExportButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 rounded-xl border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
    >
      <FileSpreadsheet className="h-3.5 w-3.5" />
      Export Excel
    </button>
  );
}

export default function ProcurementReportsPage() {
  const [downloadingMaster, setDownloadingMaster] = useState(false);

  async function downloadMasterReport() {
    setDownloadingMaster(true);
    try {
      const blob = await inventoryApi.exportMasterReport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventory-master-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingMaster(false);
    }
  }

  const { data: statusCounts, loading: statusLoading, error: statusError } = useAsyncData(async () => {
    const results = await Promise.all(
      STATUS_LIST.map((s) => inventoryApi.listMine(0, 1).then((r) => ({ status: s, count: r.totalElements }))),
    );
    return results;
  }, []);

  const { data: stockAlerts, loading: alertsLoading, error: alertsError } = useAsyncData(
    () => inventoryApi.listItems({ page: 0, size: 100 }).then((r) => r.content.filter((i) => i.belowReorderLevel)),
    [],
  );

  // Filter dropdown only needs categories still in use — inactive ones would just be dead ends.
  const categories = useAsyncData(() => inventoryCategoryApi.list({ active: true }), []);

  const [alertCategoryId, setAlertCategoryId] = useState<number | "">("");
  const [alertYear, setAlertYear] = useState<number | "ALL">("ALL");
  const [alertSort, setAlertSort] = useState<{ key: "currentStockUnits" | "reorderLevelInIssueUnits" | "deficit"; dir: "asc" | "desc" } | null>(null);
  const [alertPage, setAlertPage] = useState(0);
  const [alertSize, setAlertSize] = useState(10);
  const loading = statusLoading || alertsLoading;

  // Years actually present on loaded items' last-updated timestamp — dynamic so next year's
  // data shows up automatically without a code change, same pattern as Procurement Reports.
  const alertYears = useMemo(() => {
    const years = new Set<number>([new Date().getFullYear()]);
    for (const item of stockAlerts ?? []) {
      if (item.updatedAt) years.add(new Date(item.updatedAt).getFullYear());
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [stockAlerts]);

  /** Some items come back from the list endpoint with no reorder level set — treat that as
   *  "unknown" rather than letting the subtraction silently produce NaN. */
  function deficitOf(item: { reorderLevelInIssueUnits: number | null; currentStockUnits: number }): number | null {
    if (item.reorderLevelInIssueUnits == null) return null;
    return item.reorderLevelInIssueUnits - (item.currentStockUnits || 0);
  }

  function toggleAlertSort(key: "currentStockUnits" | "reorderLevelInIssueUnits" | "deficit") {
    setAlertSort((current) => {
      if (!current || current.key !== key) return { key, dir: "desc" };
      if (current.dir === "desc") return { key, dir: "asc" };
      return null;
    });
  }

  const filteredAlerts = useMemo(() => {
    let rows = (stockAlerts ?? []).filter((item) => {
      if (alertCategoryId && item.categoryId !== alertCategoryId) return false;
      if (alertYear !== "ALL") {
        if (!item.updatedAt || new Date(item.updatedAt).getFullYear() !== alertYear) return false;
      }
      return true;
    });
    if (alertSort) {
      const { key, dir } = alertSort;
      rows = [...rows].sort((a, b) => {
        const va = (key === "deficit" ? deficitOf(a) : a[key]) ?? 0;
        const vb = (key === "deficit" ? deficitOf(b) : b[key]) ?? 0;
        return dir === "asc" ? va - vb : vb - va;
      });
    }
    return rows;
  }, [stockAlerts, alertCategoryId, alertYear, alertSort]);

  // Reset to page 1 whenever the filters/sort change underneath the current page.
  useEffect(() => { setAlertPage(0); }, [alertCategoryId, alertYear, alertSort]);

  const alertTotalPages = Math.max(1, Math.ceil(filteredAlerts.length / alertSize));
  const pagedAlerts = filteredAlerts.slice(alertPage * alertSize, alertPage * alertSize + alertSize);
  const alertPageResponse: PageResponse<InventoryItem> = {
    content: pagedAlerts,
    totalElements: filteredAlerts.length,
    totalPages: alertTotalPages,
    number: alertPage,
    size: alertSize,
    first: alertPage === 0,
    last: alertPage + 1 >= alertTotalPages,
  };

  const exportStatusSummary = () => {
    if (!statusCounts) return;
    downloadXls(
      `request-status-summary-${new Date().toISOString().slice(0, 10)}`,
      "Request Status Summary",
      ["Status", "Count"],
      statusCounts.map((s) => [s.status.replaceAll("_", " "), s.count]),
    );
  };

  const exportStockAlerts = () => {
    downloadXls(
      `stock-alerts-${new Date().toISOString().slice(0, 10)}`,
      "Stock Alerts",
      ["Code", "Name", "Category", "Issue Stock", "Reorder Level", "Deficit"],
      filteredAlerts.map((item) => {
        const deficit = deficitOf(item);
        return [
          item.code,
          item.name,
          item.categoryName || "-",
          `${Number(item.currentStockUnits || 0).toLocaleString()} ${item.issueUnitName}`,
          item.reorderLevelInIssueUnits != null ? `${item.reorderLevelInIssueUnits} ${item.issueUnitName}` : "Not set",
          deficit != null ? `-${Math.abs(deficit).toLocaleString()} ${item.issueUnitName}` : "—",
        ];
      }),
    );
  };

  return (
    <div className="space-y-6">
      <InventoryPageHeader
        title="Inventory Reports"
        actions={
          <ExportButton onClick={downloadMasterReport} disabled={downloadingMaster} />
        }
      />

      {loading && <InventoryLoading label="Loading reports..." />}

      {/* Request Status Summary */}
      <div className="rounded-2xl border bg-white shadow-sm">
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
          <div>
            <h2 className="font-bold text-slate-900">Request Status Summary</h2>
          </div>
          {!statusLoading && statusCounts && <ExportButton onClick={exportStatusSummary} />}
        </div>
        {statusError && <div className="p-5"><InventoryError message={statusError} /></div>}
        {!statusLoading && statusCounts && (
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-6">
            {statusCounts.map(({ status, count }) => {
              const meta = STATUS_META[status] ?? { icon: FileEdit, tone: "text-slate-500 bg-slate-50" };
              const Icon = meta.icon;
              return (
                <div
                  key={status}
                  className="flex flex-col items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-4 text-center transition hover:border-slate-200 hover:shadow-sm"
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full ${meta.tone}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-2xl font-bold text-slate-900">{count}</span>
                  <InventoryStatusBadge value={status} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stock Alerts */}
      <div className="rounded-2xl border bg-white shadow-sm">
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
          <div>
            <h2 className="font-bold text-slate-900">Stock Alerts</h2>
          </div>
          {!alertsLoading && stockAlerts && <ExportButton onClick={exportStockAlerts} disabled={filteredAlerts.length === 0} />}
        </div>
        {alertsError && <div className="p-5"><InventoryError message={alertsError} /></div>}
        {!alertsLoading && stockAlerts && (
          <>
            <div className="flex flex-wrap items-center gap-3 px-5 pt-4">
              <select
                className="input w-52"
                value={alertCategoryId}
                onChange={(e) => setAlertCategoryId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">All Categories</option>
                {(categories.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select
                className="input w-36"
                value={alertYear}
                onChange={(e) => setAlertYear(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}
              >
                <option value="ALL">All Years</option>
                {alertYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              {alertSort && (
                <button
                  type="button"
                  onClick={() => setAlertSort(null)}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                >
                  Clear sort
                </button>
              )}
            </div>
            {filteredAlerts.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-slate-500">
                {stockAlerts.length === 0 ? "All items are above their reorder levels." : "No stock alerts match the selected filters."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Code</th>
                      <th className="px-5 py-3">Name</th>
                      <SortableHeader label="Issue Stock" sortKey="currentStockUnits" active={alertSort} onClick={toggleAlertSort} />
                      <SortableHeader label="Reorder Level" sortKey="reorderLevelInIssueUnits" active={alertSort} onClick={toggleAlertSort} />
                      <SortableHeader label="Deficit" sortKey="deficit" active={alertSort} onClick={toggleAlertSort} />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pagedAlerts.map((item) => {
                      const deficit = deficitOf(item);
                      return (
                        <tr key={item.id} className="border-l-2 border-l-amber-400 hover:bg-slate-50">
                          <td className="px-5 py-3 font-semibold text-slate-900">{item.code}</td>
                          <td className="px-5 py-3 font-medium text-slate-800">{item.name}</td>
                          <td className="px-5 py-3 text-amber-700 font-semibold">
                            {Number(item.currentStockUnits || 0).toLocaleString()} {item.issueUnitName}
                          </td>
                          <td className="px-5 py-3 text-slate-600">
                            {item.reorderLevelInIssueUnits != null
                              ? `${item.reorderLevelInIssueUnits} ${item.issueUnitName}`
                              : <span className="italic text-slate-400">Not set</span>}
                          </td>
                          <td className="px-5 py-3 font-bold text-red-600">
                            {deficit != null
                              ? `-${Math.abs(deficit).toLocaleString()} ${item.issueUnitName}`
                              : <span className="italic text-slate-400 font-normal">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {filteredAlerts.length > 0 && (
              <div className="border-t px-5 py-3">
                <InventoryPagination
                  page={alertPage}
                  setPage={setAlertPage}
                  size={alertSize}
                  setSize={(s) => { setAlertSize(s); setAlertPage(0); }}
                  data={alertPageResponse}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SortableHeader({
  label, sortKey, active, onClick,
}: {
  label: string;
  sortKey: "currentStockUnits" | "reorderLevelInIssueUnits" | "deficit";
  active: { key: string; dir: "asc" | "desc" } | null;
  onClick: (key: "currentStockUnits" | "reorderLevelInIssueUnits" | "deficit") => void;
}) {
  const isActive = active?.key === sortKey;
  return (
    <th className="px-5 py-3">
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`flex items-center gap-1 font-semibold uppercase tracking-wide hover:text-slate-700 ${isActive ? "text-emerald-700" : ""}`}
      >
        {label}
        <ArrowUpDown className="h-3 w-3" />
        {isActive && <span className="normal-case">({active.dir === "asc" ? "low→high" : "high→low"})</span>}
      </button>
    </th>
  );
}
